import { z } from "zod";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CATALOGUE D'ÉVÉNEMENTS DE NOTIFICATION — SOURCE UNIQUE DE VÉRITÉ
 * ─────────────────────────────────────────────────────────────────────────────
 *  Partagé client/serveur. Tout nouvel événement de notification doit être :
 *   1. déclaré ici (identifiant + catégorie),
 *   2. enregistré dans le registry serveur (`apps/server/src/notifications/core/event-registry.ts`)
 *      avec ses canaux par défaut, son icône et son deep-link,
 *   3. décrit côté client (`apps/client/src/services/notification/event-display.tsx`)
 *      pour l'affichage (icône, couleur, libellés i18n).
 *
 *  Ajouter une notification = 1 déclaration + 1 émission `eventEmitter.emit`.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const NOTIFICATION_CATEGORIES = [
  "transactional", // confirmations d'actions utilisateur (paiement, retrait…)
  "finance", // wallet, crédits, souscriptions, facturation
  "referral", // parrainage & ambassadeur (fonctionnalités utilisateur)
  "ai", // scoring ATS, traduction, exports
  "account", // sécurité & cycle de vie du compte
  "social", // vues CV, partages, engagement
  "system", // annonces système et maintenance
  "campaign", // communications marketing (console admin)
  "lifecycle", // notifications de cycle de vie (onboarding, rétention, fidélité)
] as const;

export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const NOTIFICATION_CHANNELS = ["inApp", "push", "email"] as const;
export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NOTIFICATION_PRIORITIES = ["low", "normal", "high", "critical"] as const;
export const NotificationPrioritySchema = z.enum(NOTIFICATION_PRIORITIES);
export type NotificationPriority = (typeof NOTIFICATION_PRIORITIES)[number];

/**
 * Identifiants d'événements — préfixés par domaine pour la lisibilité et
 * la traçabilité (logs, métriques, grep).
 */
export const NOTIFICATION_EVENTS = [
  // ── Paiements & souscriptions (finance) ──────────────────────────────────
  "PAYMENT_SUCCESS",
  "PAYMENT_FAILED",
  "SOUSCRIPTION_ACTIVATED",
  "SOUSCRIPTION_EXPIRING",
  "SOUSCRIPTION_EXPIRED",
  // ── Crédits (finance) ────────────────────────────────────────────────────
  "CREDIT_GRANTED",
  "CREDIT_LOW",
  "CREDIT_DEPLETED",
  "CREDIT_CONSUMED",
  // ── Wallet (finance) ─────────────────────────────────────────────────────
  "WALLET_CREDIT",
  "WALLET_DEBIT",
  // ── Retraits (transactional) ─────────────────────────────────────────────
  "WITHDRAWAL_SUBMITTED",
  "WITHDRAWAL_APPROVED",
  "WITHDRAWAL_REFUSED",
  // ── Parrainage / ambassadeur (referral) ──────────────────────────────────
  "REFERRAL_REGISTERED",
  "REFERRAL_REWARD_MONEY",
  "REFERRAL_REWARD_SOUSCRIPTION",
  "AMBASSADOR_COMMISSION_EARNED",
  // ── Fidélité (loyalty) ───────────────────────────────────────────────────
  "LOYALTY_POINTS_EARNED",
  "LOYALTY_REWARD_REDEEMED",
  // ── IA & production CV (ai) ──────────────────────────────────────────────
  "ATS_SCORE_READY",
  "TRANSLATION_READY",
  "PRINT_FAILED",
  "DOWNLOAD_READY",
  // ── Compte & sécurité (account) ──────────────────────────────────────────
  "WELCOME",
  "EMAIL_VERIFIED",
  "PASSWORD_CHANGED",
  "TWO_FACTOR_ENABLED",
  "TWO_FACTOR_DISABLED",
  "ACCOUNT_STATUS_CHANGED",
  // ── Engagement (social) ──────────────────────────────────────────────────
  "RESUME_VIEWED",
  "RESUME_SHARED",
  // ── Système & annonces (system) ──────────────────────────────────────────
  "SYSTEM_ANNOUNCEMENT",
  // ── Campagnes admin marketing (campaign) ────────────────────────────────
  "CAMPAIGN",
  // ── Alertes admin (system) ───────────────────────────────────────────────
  "ADMIN_WITHDRAWAL_REQUEST",
  "ADMIN_PAYMENT_ALERT",
  "ADMIN_SIGNUP_ALERT",
  // -- Centre d aide --
  "HELP_REQUEST_OPENED",
  "HELP_REQUEST_REPLY",
  "ADMIN_HELP_ALERT",
  // -- Import LinkedIn --
  "LINKEDIN_IMPORT_READY",
  "LINKEDIN_IMPORT_FAILED",
  // ── Cycle de vie & rappels comportementaux (lifecycle) ───────────────────
  "LIFECYCLE_ONBOARDING_J1",
  "LIFECYCLE_CV_CREATED_NO_DOWNLOAD",
  "LIFECYCLE_CV_NOT_DOWNLOADED_J3",
  "LIFECYCLE_EMAIL_UNVERIFIED_J1",
  "LIFECYCLE_PUSH_NOT_ENABLED",
  "LIFECYCLE_SUBSCRIBER_LAPSED",
  "LIFECYCLE_IDLE_J7",
] as const;

export const NotificationTypeSchema = z.enum(NOTIFICATION_EVENTS);
export type NotificationType = (typeof NOTIFICATION_EVENTS)[number];

/** Payload contextuel libre mais sérialisable (Json Prisma). */
export const notificationDataSchema = z.record(z.unknown()).nullable().default(null);
export type NotificationData = Record<string, unknown>;

/**
 * Payload du bus d'événements interne (EventEmitter2) :
 * les services métier émettent `this.eventEmitter.emit("notification.dispatch", payload)`.
 */
/**
 * Rendu e-mail préparé par l'appelant.
 *
 * Utilisé par les campagnes : le message est composé une seule fois (gabarit de
 * marque, blocs riches, liens absolus) puis transporté jusqu'au worker d'envoi.
 * Le canal e-mail l'utilise tel quel au lieu de reconstruire un message à
 * partir de la notification.
 */
export const notificationEmailOverrideSchema = z.object({
  subject: z.string().min(1).max(300),
  html: z.string().min(1),
  text: z.string().optional(),
  /** URL de désabonnement : en-tête List-Unsubscribe et lien du pied. */
  unsubscribeUrl: z.string().url().optional(),
  /**
   * Référence de suivi d'envoi (campagne). Présente, elle permet au canal
   * e-mail de réserver un créneau de cadence et de journaliser l'envoi dans le
   * registre d'idempotence.
   */
  delivery: z
    .object({
      campaignId: z.string().min(1),
      kind: z.enum(["user", "contact"]),
      recipientId: z.string().min(1),
    })
    .optional(),
});

export type NotificationEmailOverride = z.infer<typeof notificationEmailOverrideSchema>;

export const notificationDispatchPayloadSchema = z.object({
  userId: z.string().optional(), // destinataire principal (omis pour notifyAdmins)
  type: NotificationTypeSchema,
  data: notificationDataSchema.optional(),
  category: NotificationCategorySchema.optional(), // sinon déduit du registry
  title: z.string().optional(), // override ponctuel du titre
  body: z.string().optional(), // override ponctuel du corps
  link: z.string().optional(),
  icon: z.string().optional(),
  priority: NotificationPrioritySchema.optional(),
  dedupKey: z.string().optional(),
  expiresAt: z.coerce.date().optional(),
  channels: z.array(NotificationChannelSchema).optional(), // sinon déduit des préférences
  notifyAdmins: z.boolean().optional(), // notifie aussi la salle admin
  email: notificationEmailOverrideSchema.optional(), // rendu e-mail préparé (campagnes)
});
export type NotificationDispatchPayload = z.infer<typeof notificationDispatchPayloadSchema>;
